import LengthValue from "$src/CGS/domain/dto/core/Length/LengthValue";
import { Euler, Object3D, Quaternion, Vector3 } from "three";
import { EHandlePosition, EOrientation, FrontHandleMetadata, HorizontalSide, VerticalSide } from "../Metadata/FrontHandleMetadata";
import { FrontElementMetadata } from "../Metadata/FrontMetadata";
import { FrontElementObject } from "../Objects/FrontElementObject";
import { HandleBoneObject } from "../Objects/HandleBoneObject";
import { LengthMaths } from "$src/CGS/domain/dto/core/Length/LengthMaths";

export abstract class BaseFrontElement {
    public static readonly HANDLE_OFFSET: number = 0.045;
    protected frontElementMetadata: FrontElementMetadata;

    public abstract get DoorThickness(): LengthValue;

    constructor(frontElementMetadata: FrontElementMetadata) {
        this.frontElementMetadata = frontElementMetadata;
    }

    protected buildHandleBones(front: FrontElementObject): void {
        const handleData = this.frontElementMetadata.Handles;
        if (!handleData) return;

        front.widthMeter = LengthMaths.getLengthValue(this.frontElementMetadata.Width).meter;
        front.heightMeter = LengthMaths.getLengthValue(this.frontElementMetadata.Height).meter;

        handleData.forEach((data) => {
            front.handleBones.push(this.buildHandleBone(front, data));
        });
    }

    protected buildHandleBone(front: FrontElementObject, data: FrontHandleMetadata): HandleBoneObject {
        const handleData = data;

        let yAnchor: number;
        if (handleData.VerticalAlign === VerticalSide.Top) {
            yAnchor =  LengthMaths.getLengthValue(this.frontElementMetadata.Height).meter - BaseFrontElement.HANDLE_OFFSET;
        } else if (handleData.VerticalAlign === VerticalSide.Bottom) {
            yAnchor = BaseFrontElement.HANDLE_OFFSET;
        } else {
            yAnchor = LengthMaths.getLengthValue(this.frontElementMetadata.Height).meter * 0.5;
        }

        let xAnchor: number;
        if (handleData.HorizontalAlign === HorizontalSide.Right) {
            xAnchor = LengthMaths.getLengthValue(this.frontElementMetadata.Width).meter - BaseFrontElement.HANDLE_OFFSET;
        } else if (handleData.HorizontalAlign === HorizontalSide.Left) {
            xAnchor = BaseFrontElement.HANDLE_OFFSET;
        } else {
            xAnchor = LengthMaths.getLengthValue(this.frontElementMetadata.Width).meter * 0.5;
        }

        let rotation: number;
        if (handleData.Orientation === EOrientation.Left) {
            rotation = -90;
        } else if (handleData.Orientation === EOrientation.Right) {
            rotation = 90;
        } else {
            rotation = 0;
        }

        const isReversed: boolean =
            handleData.HorizontalAlign === HorizontalSide.Left ||
            handleData.VerticalAlign === VerticalSide.Bottom;

        let positionName: string = "";
        switch (handleData.Position) {
            case EHandlePosition.DefaultCenter:
                positionName = "M_center";
                break;
            case EHandlePosition.DefaultVertical:
                positionName = "B_vertical";
                break;
            case EHandlePosition.DefaultHorizontal:
                positionName = "A_horizontal";
                break;
            case EHandlePosition.CenterVertical:
                positionName = "B_centerVertical";
                break;
        }

        const name: string = isReversed ? "reverse_" + positionName + "_handle_placement_BONE" : positionName + "_handle_placement_BONE";

        const handleBone = new HandleBoneObject();
        handleBone.name = name;
        front.add(handleBone);

        handleBone.position.set(xAnchor, yAnchor, -this.DoorThickness.meter);
        const euler = new Euler(0, Math.PI, rotation * Math.PI / 180, 'XYZ');
        const quaternion = new Quaternion().setFromEuler(euler);
        handleBone.rotation.setFromQuaternion(quaternion);

        handleBone.isReversed = isReversed;
        handleBone.isHorizontal = handleData.Orientation === EOrientation.Normal;
        handleBone.handlePosition = handleData.Position;
        handleBone.orientation = handleData.Orientation;
        handleBone.horizontalAlign = handleData.HorizontalAlign;
        handleBone.verticalAlign = handleData.VerticalAlign;

        return handleBone;
    }
}
